/**
 * Unified storage utility for consistent token and user data handling.
 * Uses localStorage so the login is shared by every tab: sessionStorage is
 * per-tab, which forced a fresh login whenever a link (e.g. from an email)
 * opened the app in a new tab.
 */

const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth-token',
  CURRENT_USER: 'currentUser',
  USER_DATA: 'userData',
} as const;

/**
 * One-time move of a login stored by older builds in sessionStorage, so tabs
 * that were already logged in when this version shipped stay logged in.
 */
const migrateFromSessionStorage = (): void => {
  try {
    Object.values(STORAGE_KEYS).forEach((key) => {
      const value = sessionStorage.getItem(key);
      if (value !== null) {
        if (localStorage.getItem(key) === null) localStorage.setItem(key, value);
        sessionStorage.removeItem(key);
      }
    });
  } catch {
    // Storage unavailable (private mode / blocked) — nothing to migrate.
  }
};
migrateFromSessionStorage();

export interface StoredUser {
  userId: number;
  userEmail: string;
  userName: string;
  userRole: string;
  token: string;
}

/**
 * Get the authentication token from local storage
 */
export const getAuthToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Set the authentication token in local storage
 */
export const setAuthToken = (token: string): void => {
  localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
};

/**
 * Remove the authentication token from local storage
 */
export const removeAuthToken = (): void => {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Get the current user from local storage
 */
export const getCurrentUser = (): StoredUser | null => {
  const userStr = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
  if (!userStr) return null;

  try {
    return JSON.parse(userStr) as StoredUser;
  } catch {
    return null;
  }
};

/**
 * Set the current user in local storage
 */
export const setCurrentUser = (user: StoredUser): void => {
  localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
};

/**
 * Remove the current user from local storage
 */
export const removeCurrentUser = (): void => {
  localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
};

/**
 * Get user data from local storage
 */
export const getUserData = (): StoredUser | null => {
  const userStr = localStorage.getItem(STORAGE_KEYS.USER_DATA);
  if (!userStr) return null;

  try {
    return JSON.parse(userStr) as StoredUser;
  } catch {
    return null;
  }
};

/**
 * Set user data in local storage
 */
export const setUserData = (user: StoredUser): void => {
  localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(user));
};

/**
 * Remove user data from local storage
 */
export const removeUserData = (): void => {
  localStorage.removeItem(STORAGE_KEYS.USER_DATA);
};

/**
 * Check if user is authenticated (has valid token)
 */
export const isAuthenticated = (): boolean => {
  const token = getAuthToken();
  return token !== null && token.length > 0;
};

/**
 * Clear all authentication data from local storage
 */
export const clearAuth = (): void => {
  removeAuthToken();
  removeCurrentUser();
  removeUserData();
};

/**
 * Swap in a renewed token (sliding session) without touching the rest of the login.
 * Other tabs pick it up through the storage event.
 */
export const replaceAuthToken = (token: string): void => {
  if (!token || !getAuthToken()) return;
  setAuthToken(token);
  const current = getCurrentUser();
  if (current) setCurrentUser({ ...current, token });
  const data = getUserData();
  if (data) setUserData({ ...data, token });
};

/**
 * Store all authentication data after successful login
 */
export const storeAuthData = (token: string, user: StoredUser): void => {
  setAuthToken(token);
  setCurrentUser(user);
  setUserData(user);
};

export default {
  getAuthToken,
  setAuthToken,
  removeAuthToken,
  getCurrentUser,
  setCurrentUser,
  removeCurrentUser,
  getUserData,
  setUserData,
  removeUserData,
  isAuthenticated,
  clearAuth,
  storeAuthData,
  replaceAuthToken,
  STORAGE_KEYS,
};
